import type { Login } from "@/domain/usecase/Login";
import type { LoginForm } from "./schemas";
import { useCallback } from "react";
import { mapFormToLoginRequest } from "../mappers";
import { useMutation } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";

type UiState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "success" };

type LoginResult = { type: "SUCCESS" } | { type: "FAILED" };

function toUiState(
  mutation: UseMutationResult<void, Error, LoginForm>,
): UiState {
  switch (mutation.status) {
    case "pending":
      return { status: "loading" };

    case "error":
      return {
        status: "error",
        message: mutation.error.message,
      };

    case "success":
      return { status: "success" };

    case "idle":
      return { status: "idle" };
  }
}

export function useLoginViewModel(login: Login) {
  const loginMutation = useMutation({
    mutationFn: async (form: LoginForm) => {
      const req = mapFormToLoginRequest(form);
      await login.execute(req);
    },
  });

  const submit = useCallback(
    async (form: LoginForm): Promise<LoginResult> => {
      try {
        await loginMutation.mutateAsync(form);

        return { type: "SUCCESS" };
      } catch {
        return { type: "FAILED" };
      }
    },
    [loginMutation],
  );

  const state = toUiState(loginMutation)

  return { state, intents: { submit } };
}
